/* Sibling color swatches (one product per color).

   context="collection":
     - hover/focus a swatch -> preview that color's image in this card and
       retarget the card's links to that product
     - clicking a swatch does NOT navigate (preview only); clicking the card
       navigates to whichever color is currently previewed
   context="product":
     - clicking a swatch navigates straight to that color's product
*/
(function () {
  if (window.customElements && window.customElements.get('sibling-swatches')) return;

  class SiblingSwatches extends HTMLElement {
    connectedCallback() {
      this.context = this.getAttribute('data-context') || 'collection';
      this.defaultUrl = this.getAttribute('data-default-url');
      this.items = Array.from(this.querySelectorAll('.product-block-options__item--sibling'));

      if (this.context === 'product') {
        this.initProduct();
      } else {
        this.initCollection();
      }
    }

    /* ---------- Collection: preview only ---------- */
    initCollection() {
      this.card = this.closest('product-block') || this.closest('.product-block');
      if (!this.card) return;

      this.imageHost = this.card.querySelector(
        '.product-block__image--primary .theme-img, .product-block__image--primary img'
      );
      this.productLinks = Array.from(this.card.querySelectorAll('a.product-link'));
      this.originalSrc = this.imageHost ? (this.imageHost.currentSrc || this.imageHost.src) : null;
      this.originalSrcset = this.imageHost ? this.imageHost.getAttribute('srcset') : null;

      // Label host: where product labels (sale/new/stock) live over the image.
      // Keep a handle to the container (creating one if the base product has no
      // labels) plus its original markup so we can swap per-sibling and restore.
      this.labelWrap = this.card.querySelector('.image-label-wrap');
      this.labelContainer = this.card.querySelector('.product-label-container');
      this.originalLabels = this.labelContainer ? this.labelContainer.innerHTML : null;

      this.items.forEach((item) => {
        // Preview on hover/focus
        item.addEventListener('mouseenter', () => this.preview(item));
        item.addEventListener('focus', () => this.preview(item));
        // On click/tap: preview only, do NOT follow the link
        item.addEventListener('click', (e) => {
          e.preventDefault();
          this.preview(item);
        });
      });

      this.addEventListener('mouseleave', () => this.restore());
    }

    preview(item) {
      const img = item.getAttribute('data-sibling-image');
      const url = item.getAttribute('data-sibling-url');
      const alt = item.getAttribute('data-sibling-image-alt');

      if (this.imageHost && img) {
        this.imageHost.removeAttribute('srcset');
        this.imageHost.removeAttribute('data-manual-srcset');
        this.imageHost.src = img;
        this.imageHost.setAttribute('data-manual-src', img);
        if (alt) this.imageHost.alt = alt;
      }
      if (url) {
        this.productLinks.forEach((a) => { a.href = url; });
      }
      this.swapLabels(item.getAttribute('data-sibling-labels'));
      this.items.forEach((i) =>
        i.classList.toggle('product-block-options__item--active', i === item)
      );
    }

    // Replace the card's product labels with the previewed sibling's labels.
    // `html` is the full .product-label-container markup (or empty/null if the
    // sibling has no labels). Creates the container on demand when needed.
    swapLabels(html) {
      const markup = (html || '').trim();
      if (!this.labelContainer && this.labelWrap && markup) {
        const tmp = document.createElement('div');
        tmp.innerHTML = markup;
        this.labelContainer = tmp.firstElementChild;
        if (this.labelContainer) {
          this.labelWrap.appendChild(this.labelContainer);
          this.originalLabels = ''; // base had none -> restore to empty
        }
        return;
      }
      if (!this.labelContainer) return;
      // markup includes the wrapping .product-label-container; use its inner.
      if (markup) {
        const tmp = document.createElement('div');
        tmp.innerHTML = markup;
        const inner = tmp.firstElementChild;
        this.labelContainer.innerHTML = inner ? inner.innerHTML : '';
        this.labelContainer.style.display = '';
      } else {
        this.labelContainer.innerHTML = '';
        this.labelContainer.style.display = 'none';
      }
    }

    restore() {
      // Keep the last previewed selection sticky so the card click still works.
      // Only restore the image if nothing was actively chosen.
      const active = this.querySelector('.product-block-options__item--active');
      if (active) return; // leave preview in place
      if (this.imageHost) {
        if (this.originalSrcset) this.imageHost.setAttribute('srcset', this.originalSrcset);
        if (this.originalSrc) this.imageHost.src = this.originalSrc;
      }
      if (this.defaultUrl) {
        this.productLinks.forEach((a) => { a.href = this.defaultUrl; });
      }
      if (this.labelContainer && this.originalLabels !== null) {
        this.labelContainer.innerHTML = this.originalLabels;
        this.labelContainer.style.display = this.originalLabels.trim() === '' ? 'none' : '';
      }
    }

    /* ---------- Product page: navigate ---------- */
    initProduct() {
      // Signal that product-page color siblings are active so CSS can hide
      // the redundant native color row in the variant picker.
      document.body.classList.add('has-sibling-color-swatches');

      // Swatches are plain <a> tags; let the browser navigate normally.
      // Just mark the current one active for styling.
      this.items.forEach((item) => {
        if (item.classList.contains('product-block-options__item--current')) {
          item.classList.add('product-block-options__item--active');
        }
      });
    }
  }

  window.customElements.define('sibling-swatches', SiblingSwatches);
})();